import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faTrash, faCheck, faTimes, faChevronDown, faChevronRight, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { useAuth } from '../../../contexts/AuthContext';
import BudgetTemplateModal from './BudgetTemplateModal';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', bg: '#f3f4f6', color: '#6b7280' },
  submitted: { label: 'Soumis', bg: '#fef9c3', color: '#a16207' },
  approved: { label: 'Approuvé', bg: '#dcfce7', color: '#166534' },
  rejected: { label: 'Rejeté', bg: '#fee2e2', color: '#991b1b' },
};

const CATEGORY_LABELS = {
  enqueteurs_cati: 'Enquêteurs CATI', enqueteurs_f2f: 'Prestataires F2F',
  superviseurs: 'Prestataires F2F', controle: 'Contrôle', deplacement: 'Déplacement',
  imprevus: 'Imprévus', autre: 'Autre',
};

const BudgetTab = ({ projectId, project, readOnly }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isContrat = project?.project_type === 'contrat';
  const soldeGlobal = project?.solde_global ? parseFloat(project.solde_global) : null;
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [templateModal, setTemplateModal] = useState({ open: false, slotId: null });
  const [alert, setAlert] = useState(null);

  const canCreate = ['charge_etude', 'charge_etude_superieur', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur', 'resp_admin'].includes(user?.role);
  const canApprove = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [budgetRes, slotRes] = await Promise.all([
        api.get(`/projects/${projectId}/budgets`),
        api.get(`/projects/${projectId}/slots`),
      ]);
      setBudgets(budgetRes.data.data?.budgets || []);
      setSummary(budgetRes.data.data?.summary || null);
      const allSlots = [
        ...(slotRes.data.data?.waves || []).flatMap(w => (w.survey_slots || []).map(s => ({ ...s, waveName: w.name }))),
        ...(slotRes.data.data?.standalone_slots || []).map(s => ({ ...s, waveName: null })),
      ];
      setSlots(allSlots);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApplyTemplate = async (slotId, templateLines) => {
    setSaving(true); setAlert(null);
    try {
      const lines = templateLines.map(l => ({
        category: l.category, label: l.label,
        qty: l.qty, unit_price: l.unit_price, notes: l.notes || null,
      }));
      const res = await api.post(`/projects/${projectId}/budgets`, { survey_slot_id: slotId, lines });
      setTemplateModal({ open: false, slotId: null });
      if (res.data.data?.id) {
        navigate(`/projects/${projectId}/budgets/${res.data.data.id}/edit`);
      }
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (budgetId) => {
    try {
      await api.delete(`/projects/${projectId}/budgets/${budgetId}`);
      fetchData();
      setAlert({ type: 'success', text: 'Budget supprimé' });
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
  };



  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" /></div>;

  return (
    <div>
      {alert && (
        <div style={{
          padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.8rem',
          background: alert.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${alert.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: alert.type === 'success' ? '#166534' : '#991b1b',
        }}>{alert.text}</div>
      )}

      {/* Project Summary */}
      {summary && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 16,
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
            Budget global du projet
          </div>
          <div className="budget-metrics-grid">
            <div style={{ textAlign: 'center', padding: '10px 0', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Coût total estimé</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>{Number(summary.total_cost).toLocaleString('fr-FR')}</div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>MAD</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Montant approuvé</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#10b981' }}>{Number(summary.approved_cost).toLocaleString('fr-FR')}</div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>MAD</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Budgets</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>{summary.approved_count}<span style={{ color: '#9ca3af', fontWeight: 400 }}>/{summary.budgets_count}</span></div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>approuvé(s)</div>
            </div>
          </div>
        </div>
      )}

      {/* Solde Contrat */}
      {isContrat && soldeGlobal && summary && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 16,
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
            Solde Contrat
          </div>
          <div className="budget-metrics-grid" style={{ marginBottom: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Solde global</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>{soldeGlobal.toLocaleString('fr-FR')}</div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>MAD</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Budgets alloués</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: summary.total_cost > soldeGlobal ? '#ef4444' : '#111827' }}>
                {Number(summary.total_cost).toLocaleString('fr-FR')}
              </div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>MAD</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Restant</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: (soldeGlobal - summary.total_cost) < 0 ? '#ef4444' : '#10b981' }}>
                {(soldeGlobal - summary.total_cost).toLocaleString('fr-FR')}
              </div>
              <div style={{ fontSize: '0.55rem', color: '#9ca3af' }}>MAD</div>
            </div>
          </div>
          <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, (summary.total_cost / soldeGlobal) * 100)}%`, height: '100%', borderRadius: 3,
              background: (summary.total_cost / soldeGlobal) > 1 ? '#ef4444' : (summary.total_cost / soldeGlobal) > 0.9 ? '#f59e0b' : '#dc2626',
              transition: 'width 0.3s',
            }} />
          </div>
          <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
            {Math.round((summary.total_cost / soldeGlobal) * 100)}% utilisé
          </div>
        </div>
      )}

      {/* Per-slot budgets */}
      {slots.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '40px 20px', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faWallet} size="2x" style={{ color: '#e5e7eb', marginBottom: 10 }} />
          <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Aucune enquête définie. Définissez des enquêtes d'abord.</div>
        </div>
      ) : (
        slots.map(slot => {
          const budget = budgets.find(b => b.survey_slot_id === slot.id);
          const isCollapsed = collapsed[slot.id] !== false;
          const status = budget ? STATUS_CONFIG[budget.status] || STATUS_CONFIG.draft : null;

          return (
            <div key={slot.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10, overflow: 'visible' }}>
              {/* Header */}
              <div
                onClick={() => toggle(slot.id)}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 16px', cursor: 'pointer',
                  borderBottom: isCollapsed ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} size="xs" style={{ color: '#9ca3af', marginRight: 10 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                    {slot.waveName ? `${slot.waveName} › ` : ''}{slot.name}
                  </span>
                </div>
                {status && (
                  <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: status.bg, color: status.color, marginRight: 8 }}>
                    {status.label}
                  </span>
                )}
                {budget && (
                  <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#374151', marginRight: 8 }}>
                    {Number(budget.total_cost).toLocaleString('fr-FR')} MAD
                  </span>
                )}
                {/* Actions */}
                {!readOnly && <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                  {!budget && canCreate && (
                    <>
                      <button onClick={() => navigate(`/projects/${projectId}/budgets/create?slot=${slot.id}`)}
                        style={{ fontSize: '0.68rem', fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                        Créer
                      </button>
                      <button onClick={() => setTemplateModal({ open: true, slotId: slot.id })}
                        style={{ fontSize: '0.68rem', padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                        Modèle
                      </button>
                    </>
                  )}
                  {budget && (budget.status === 'draft' || budget.status === 'rejected') && canCreate && (
                    <button onClick={() => navigate(`/projects/${projectId}/budgets/${budget.id}/edit`)}
                      style={{ fontSize: '0.68rem', padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>
                      Modifier
                    </button>
                  )}
                  {budget && (budget.status === 'draft' || budget.status === 'rejected') && canCreate && (
                    <button onClick={() => handleDelete(budget.id)}
                      style={{ fontSize: '0.68rem', padding: '3px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faTrash} size="xs" />
                    </button>
                  )}
                  {budget && budget.status === 'submitted' && canApprove && (
                    <button onClick={() => navigate(`/projects/${projectId}/budgets/${budget.id}/review`)}
                      style={{ fontSize: '0.68rem', fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" /> Examiner
                    </button>
                  )}
                </div>}
              </div>

              {/* Body */}
              {!isCollapsed && (
                <div style={{ padding: '12px 16px' }}>
                  {budget ? (
                    <BudgetReadOnly budget={budget} />
                  ) : (
                    <div style={{ fontSize: '0.78rem', color: '#d1d5db', fontStyle: 'italic', padding: '10px 0' }}>
                      Aucun budget défini pour cette enquête.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      <BudgetTemplateModal
        isOpen={templateModal.open}
        onClose={() => setTemplateModal({ open: false, slotId: null })}
        currentProjectId={parseInt(projectId)}
        onApply={(lines) => handleApplyTemplate(templateModal.slotId, lines)}
      />

      <style>{`
        .budget-metrics-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        @media (max-width: 768px) {
          .budget-metrics-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

const BudgetReadOnly = ({ budget }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: '#f9fafb', borderRadius: 8 }}>
      <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>Coût total:</span>
      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>{Number(budget.total_cost).toLocaleString('fr-FR')} MAD</span>
    </div>

    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Catégorie</th>
            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Libellé</th>
            <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Qté/Jrs</th>
            <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>P.U.</th>
            <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {(budget.lines || []).map((line, i) => (
            <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 10px' }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#f3f4f6', color: '#374151' }}>
                  {CATEGORY_LABELS[line.category] || line.category}
                </span>
              </td>
              <td style={{ padding: '6px 10px', color: '#111827' }}>{line.label || '—'}</td>
              <td style={{ padding: '6px 10px', textAlign: 'right' }}>{line.qty}</td>
              <td style={{ padding: '6px 10px', textAlign: 'right' }}>{Number(line.unit_price).toLocaleString('fr-FR')}</td>
              <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{Number(line.total).toLocaleString('fr-FR')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default BudgetTab;
